/**
 * Where a markdown string has code (fenced blocks and inline code spans), found in one linear pass
 * so text rewrites can leave code alone. An approximation of CommonMark that errs on the side of
 * "code": fences may be indented any amount, an unclosed fence runs to the end, and a code span is
 * a backtick run closed by the next run of the same length in the same paragraph. Backslash
 * escapes and indented (4-space) code blocks are not recognised.
 */

/** Half-open `[start, end)` character range. */
export type CodeRange = readonly [number, number]

const FENCE_RE = /^[ \t]*(`{3,}|~{3,})(.*)$/

interface Line {
  start: number
  end: number
  text: string
}

function splitLines(markdown: string): Line[] {
  const lines: Line[] = []
  let start = 0
  while (start <= markdown.length) {
    const nl = markdown.indexOf('\n', start)
    const end = nl === -1 ? markdown.length : nl
    lines.push({start, end, text: markdown.slice(start, end)})
    if (nl === -1) break
    start = nl + 1
  }
  return lines
}

function openingFence(text: string): {char: string; length: number} | null {
  const match = FENCE_RE.exec(text)
  if (!match) return null
  const fence = match[1]
  // A backtick fence's info string cannot contain backticks (that is inline code instead).
  if (fence[0] === '`' && match[2].includes('`')) return null
  return {char: fence[0], length: fence.length}
}

function closesFence(text: string, open: {char: string; length: number}): boolean {
  const match = FENCE_RE.exec(text)
  return !!match && match[1][0] === open.char && match[1].length >= open.length && !match[2].trim()
}

/** Code spans of one paragraph (`[from, to)` of the markdown). */
function inlineCodeRanges(markdown: string, from: number, to: number, out: CodeRange[]): void {
  const runs: Array<{pos: number; len: number}> = []
  for (let i = from; i < to; i++) {
    if (markdown[i] !== '`') continue
    let j = i
    while (j < to && markdown[j] === '`') j++
    runs.push({pos: i, len: j - i})
    i = j - 1
  }
  // Run indices by length, with a moving pointer per length: each closer lookup is amortised O(1).
  const byLength = new Map<number, number[]>()
  runs.forEach((run, index) => {
    const list = byLength.get(run.len)
    if (list) list.push(index)
    else byLength.set(run.len, [index])
  })
  const pointers = new Map<number, number>()
  let i = 0
  while (i < runs.length) {
    const {pos, len} = runs[i]
    const candidates = byLength.get(len)!
    let p = pointers.get(len) ?? 0
    while (p < candidates.length && candidates[p] <= i) p++
    pointers.set(len, p)
    if (p < candidates.length) {
      const close = runs[candidates[p]]
      out.push([pos, close.pos + close.len])
      i = candidates[p] + 1
    } else {
      i++
    }
  }
}

/** Sorted, non-overlapping code ranges of `markdown`. */
export function markdownCodeRanges(markdown: string): CodeRange[] {
  const ranges: CodeRange[] = []
  const lines = splitLines(markdown)
  let paragraphStart: number | null = null
  const endParagraph = (end: number) => {
    if (paragraphStart !== null) inlineCodeRanges(markdown, paragraphStart, end, ranges)
    paragraphStart = null
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const fence = openingFence(line.text)
    if (fence) {
      endParagraph(line.start)
      let j = i + 1
      while (j < lines.length && !closesFence(lines[j].text, fence)) j++
      const last = lines[Math.min(j, lines.length - 1)]
      ranges.push([line.start, last.end])
      i = j
      continue
    }
    if (!line.text.trim()) {
      endParagraph(line.start)
    } else if (paragraphStart === null) {
      paragraphStart = line.start
    }
  }
  endParagraph(markdown.length)
  return ranges
}

/**
 * `String.replace` with a callback that only runs on matches outside code: a match that overlaps a
 * code range is kept as is. Matches arrive in order, so one moving pointer over the ranges suffices.
 */
export function replaceOutsideCode(
  markdown: string,
  pattern: RegExp,
  replacer: (match: string, offset: number, whole: string, groups: string[]) => string
): string {
  const ranges = markdownCodeRanges(markdown)
  let r = 0
  return markdown.replace(pattern, (...args: unknown[]) => {
    const match = args[0] as string
    const offsetIndex = args.findIndex((a, i) => i > 0 && typeof a === 'number')
    const offset = args[offsetIndex] as number
    const groups = args.slice(1, offsetIndex) as string[]
    while (r < ranges.length && ranges[r][1] <= offset) r++
    if (r < ranges.length && ranges[r][0] < offset + match.length) return match
    return replacer(match, offset, markdown, groups)
  })
}
